import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Save, ExternalLink, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical trade/service list — kept in sync with Landing.jsx SERVICES so
// all dropdowns across the app show the same options.
const TRADES = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Roofing",
  "Painting",
  "Landscaping",
  "Handyman",
  "Carpentry",
  "Flooring",
  "Cleaning",
  "Remodeling",
  "General Contractor",
  "Other",
];

const WORK_ZONE_OPTIONS = [
  { id: "home", label: "My hometown / immediate area" },
  { id: "25mi", label: "Within 25 miles" },
  { id: "50mi", label: "Within 50 miles" },
  { id: "100mi", label: "Within 100 miles" },
  { id: "statewide", label: "Anywhere in my state" },
  { id: "out_of_state", label: "Out-of-state for the right job" },
];

const inputCls =
  "mt-2 bg-[#010204] border border-[#C9C6C6]/12 rounded-none h-11 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0 font-normal";
const labelCls =
  "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";

export default function ProProfileEdit() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    trade: "",
    email: "",
    phone: "",
    service_area: "",
    external_profile_links: [""],
    work_zones: [],
    years_in_business: "",
    team_size: "",
  });
  const [originalEmail, setOriginalEmail] = useState("");
  const [emailChangeConfirm, setEmailChangeConfirm] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/pro/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workspace/profile`);
        setForm({
          name: data.name || "",
          business_name: data.business_name || "",
          trade: data.trade || "",
          email: data.email || "",
          phone: data.phone || "",
          service_area: data.service_area || "",
          external_profile_links:
            data.external_profile_links?.length ? data.external_profile_links : [""],
          work_zones: data.work_zones || [],
          years_in_business: data.years_in_business ?? "",
          team_size: data.team_size ?? "",
        });
        setOriginalEmail(data.email || "");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load profile");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLink = (i, v) =>
    setForm((f) => ({
      ...f,
      external_profile_links: f.external_profile_links.map((x, idx) => (idx === i ? v : x)),
    }));
  const addLink = () =>
    setForm((f) =>
      f.external_profile_links.length >= 5
        ? f
        : { ...f, external_profile_links: [...f.external_profile_links, ""] }
    );
  const removeLink = (i) =>
    setForm((f) => ({
      ...f,
      external_profile_links:
        f.external_profile_links.length === 1
          ? [""]
          : f.external_profile_links.filter((_, idx) => idx !== i),
    }));
  const toggleZone = (id) =>
    setForm((f) => ({
      ...f,
      work_zones: f.work_zones.includes(id)
        ? f.work_zones.filter((z) => z !== id)
        : [...f.work_zones, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.email !== originalEmail && !emailChangeConfirm) {
      toast.error("Please confirm the email change checkbox");
      return;
    }
    const payload = {
      name: form.name.trim(),
      business_name: form.business_name.trim(),
      trade: form.trade,
      email: form.email.trim(),
      phone: form.phone.trim(),
      service_area: form.service_area.trim(),
      external_profile_links: form.external_profile_links
        .map((l) => l.trim())
        .filter(Boolean),
      work_zones: form.work_zones,
      years_in_business:
        form.years_in_business === "" ? null : Number(form.years_in_business),
      team_size: form.team_size === "" ? null : Number(form.team_size),
    };
    setSaving(true);
    try {
      await axios.patch(`${API}/workspace/profile`, payload);
      toast.success("Profile updated");
      setOriginalEmail(payload.email);
      setEmailChangeConfirm(false);
      if (typeof checkAuth === "function") checkAuth();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  const emailChanged = form.email !== originalEmail;

  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF]" data-testid="pro-profile-edit">
      <header className="border-b border-[#C9C6C6]/8 bg-[#0D1421] sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
          <Link to="/workspace" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
          </Link>
          <Link
            to="/workspace"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] hover:text-[#EEEEEF]"
            data-testid="profile-back-to-workspace"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-[#0E93EC] mb-3">
            — Business profile
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[0.95] break-word-safe">
            Edit your profile.
          </h1>
          <p className="mt-3 text-[#C9C6C6] text-sm sm:text-base leading-relaxed max-w-2xl">
            Changes here update your public pro profile at{" "}
            <code className="text-[#0E93EC]">/pro/{user?.id?.slice(0, 8)}…</code> —
            what homeowners see when a pro card is shared.
          </p>
        </motion.div>

        <form onSubmit={submit} className="mt-8 sm:mt-10 space-y-8" data-testid="pro-profile-form">
          <section className="bg-[#010204] border border-[#C9C6C6]/10 p-5 sm:p-7">
            <h2 className="font-editorial text-2xl font-light tracking-tight mb-5">Basics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className={labelCls}>Your name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className={inputCls}
                  data-testid="profile-name"
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Business name *</Label>
                <Input
                  value={form.business_name}
                  onChange={(e) => set("business_name", e.target.value)}
                  className={inputCls}
                  data-testid="profile-business-name"
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Trade *</Label>
                <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
                  <SelectTrigger
                    className={inputCls}
                    data-testid="profile-trade"
                  >
                    <SelectValue placeholder="Select trade" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRADES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Service area *</Label>
                <Input
                  value={form.service_area}
                  onChange={(e) => set("service_area", e.target.value)}
                  className={inputCls}
                  data-testid="profile-service-area"
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Years in business</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.years_in_business}
                  onChange={(e) => set("years_in_business", e.target.value)}
                  className={inputCls}
                  data-testid="profile-years"
                />
              </div>
              <div>
                <Label className={labelCls}>Team size</Label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={form.team_size}
                  onChange={(e) => set("team_size", e.target.value)}
                  className={inputCls}
                  data-testid="profile-team-size"
                />
              </div>
            </div>
          </section>

          <section className="bg-[#010204] border border-[#C9C6C6]/10 p-5 sm:p-7">
            <h2 className="font-editorial text-2xl font-light tracking-tight mb-5">Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className={labelCls}>Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inputCls}
                  data-testid="profile-email"
                  required
                />
                {emailChanged && (
                  <label className="mt-2 flex items-start gap-2 text-xs text-[#EEEEEF]">
                    <input
                      type="checkbox"
                      checked={emailChangeConfirm}
                      onChange={(e) => setEmailChangeConfirm(e.target.checked)}
                      className="mt-0.5"
                      data-testid="profile-email-confirm"
                    />
                    <span>
                      Change my login email from <b>{originalEmail}</b> to{" "}
                      <b>{form.email}</b>. Next sign-in will need to match the new email.
                    </span>
                  </label>
                )}
              </div>
              <div>
                <Label className={labelCls}>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={inputCls}
                  data-testid="profile-phone"
                  required
                />
              </div>
            </div>
          </section>

          <section className="bg-[#010204] border border-[#C9C6C6]/10 p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-editorial text-2xl font-light tracking-tight">
                External profiles
              </h2>
              {form.external_profile_links.length < 5 && (
                <button
                  type="button"
                  onClick={addLink}
                  className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] hover:text-[#010204] inline-flex items-center gap-1"
                  data-testid="profile-link-add"
                >
                  <Plus className="w-3.5 h-3.5" /> Add link
                </button>
              )}
            </div>
            <div className="space-y-2" data-testid="profile-links">
              {form.external_profile_links.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-[#C9C6C6] shrink-0" />
                  <Input
                    value={v}
                    onChange={(e) => setLink(i, e.target.value)}
                    placeholder="facebook.com/yourbiz  ·  yourbiz.com"
                    className={inputCls + " flex-1 mt-0"}
                    data-testid={`profile-link-${i}`}
                  />
                  {form.external_profile_links.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLink(i)}
                      className="p-2 text-[#C9C6C6] hover:text-[#EEEEEF]"
                      aria-label="Remove link"
                      data-testid={`profile-link-remove-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#010204] border border-[#C9C6C6]/10 p-5 sm:p-7">
            <h2 className="font-editorial text-2xl font-light tracking-tight mb-4">
              Where do you want to work?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="profile-workzones">
              {WORK_ZONE_OPTIONS.map((z) => {
                const active = form.work_zones.includes(z.id);
                return (
                  <button
                    key={z.id}
                    type="button"
                    onClick={() => toggleZone(z.id)}
                    className={`text-left px-3 py-3 text-[13px] border transition-colors flex items-center gap-2 ${
                      active
                        ? "bg-[#0E93EC] text-white border-[#0E93EC]"
                        : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/15 hover:border-[#0E93EC]"
                    }`}
                    data-testid={`profile-workzone-${z.id}`}
                  >
                    <span
                      className={`inline-block w-3 h-3 shrink-0 ${
                        active ? "bg-[#0E93EC]" : "border border-[#C9C6C6]/40"
                      }`}
                    />
                    {z.label}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-end">
            <Link
              to="/workspace"
              className="inline-flex items-center justify-center h-11 px-5 border border-[#C9C6C6]/12 text-sm text-[#EEEEEF] hover:text-[#EEEEEF] hover:border-[#C9C6C6]/30"
              data-testid="profile-cancel"
            >
              Cancel
            </Link>
            <Button
              type="submit"
              disabled={saving}
              className="group bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-11 pl-5 pr-4 font-medium"
              data-testid="profile-save"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
